import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Undo2, Trash2, Sparkles, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";
import { Seo } from "@/components/Seo";
import { Burger, LayerIcon, INGREDIENTS } from "@/components/game/Burger";
import { ShareButtons } from "@/components/game/share";
import { GameOffer } from "@/components/game/GameOffer";
import { SaveToGallery } from "@/components/game/SaveToGallery";
import { sfx, SoundToggle } from "@/components/game/sound";
import { pageJsonld, PAGES } from "@/seo.mjs";
import MENUS from "@/game/menus.json";

const menus = MENUS.hamburguesa;

export default function Hamburguesa() {
  const { t } = useLang();
  const g = t.game, b = t.burger;
  const svgRef = useRef(null);
  const [stack, setStack] = useState([]);
  const [menuKey, setMenuKey] = useState(null);
  const menu = menus.find((m) => m.key === menuKey);
  const target = menu ? [...menu.ingredients].reverse() : null;
  const done = target && stack.length === target.length && stack.every((k, i) => k === target[i]);
  const nextOk = target && stack.length < target.length && stack.every((k, i) => k === target[i]);
  useEffect(() => { if (done) sfx.success(); }, [done]);

  const pick = (k) => { setMenuKey(k); setStack([]); };
  const add = (k) => {
    if (stack.length >= 12) return;
    const next = [...stack, k];
    setStack(next);
    if (target && next.length <= target.length && next.every((x, i) => x === target[i])) sfx.place();
    else if (target) sfx.wrong(); else sfx.place();
  };
  const chip = (testid, onClick, active, children) => (
    <button key={testid} data-testid={testid} onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs md:text-sm font-semibold transition-colors duration-200 ${
        active ? "bg-[#2E5A3B] text-white" : "bg-white text-[#132A1D] border border-black/10 hover:bg-[#2E5A3B]/10"}`}>
      {children}
    </button>
  );

  return (
    <div data-testid="burger-page" className="pt-16 min-h-screen bg-[#FBEFDD]">
      <Seo data={{ path: "/hamburguesa", title: b.seoTitle, description: b.seoDesc, image: PAGES.hamburguesa.image, jsonld: pageJsonld("hamburguesa") }} />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-20">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#C86A4B] font-bold mb-3">{g.kicker}</p>
            <h1 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#132A1D] tracking-tight">{b.title}</h1>
            <p className="text-[#5C5C5C] mt-4 max-w-xl leading-relaxed">{b.sub}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href="/juegos/hamburguesa/" data-testid="game-cards-page-link" className="inline-flex items-center gap-2 rounded-full border border-black/15 text-[#132A1D] px-6 py-3 text-sm font-semibold hover:bg-white transition-colors duration-300">{g.cards}</a>
            <Link to="/catalogo/hamburguesa-armar" data-testid="game-buy-link" className="inline-flex items-center gap-2 rounded-full bg-[#C86A4B] text-white px-6 py-3 text-sm font-semibold hover:bg-[#2E5A3B] transition-colors duration-300">{b.buy} <ArrowRight size={16} /></Link>
          </div>
        </div>

        <GameOffer slug="hamburguesa-armar" playPath="/hamburguesa" />

        <div className="flex flex-wrap gap-2 mb-8" data-testid="game-modes">
          {chip("mode-free", () => pick(null), !menu, g.free)}
          {menus.map((m, i) => chip(`mode-${m.key}`, () => pick(m.key), menuKey === m.key, `${i + 1}. ${m.name}`))}
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5 relative">
            <Burger svgRef={svgRef} stack={stack} testid="burger-board" className="w-full max-w-sm mx-auto rounded-2xl shadow-[0_10px_40px_-15px_rgba(120,70,20,.35)]" />
            {done && (
              <div data-testid="game-success" className="absolute inset-x-4 top-4 bg-[#2E5A3B] text-white rounded-2xl px-6 py-4 flex items-center gap-3 shadow-lg animate-in fade-in">
                <Sparkles size={22} className="text-[#F6C744]" />
                <div><p className="font-serif-d text-2xl font-semibold leading-tight">{b.success}</p><p className="text-sm text-white/80">{b.successSub}</p></div>
              </div>
            )}
            {target && !done && stack.length > 0 && !nextOk && (
              <p data-testid="burger-wrong" className="text-sm font-semibold text-[#C86A4B] mt-3 text-center">{b.wrong}</p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
              <p className="text-xs text-[#5C5C5C]" data-testid="burger-count">{stack.length} {b.layers}</p>
              <div className="flex flex-wrap items-center gap-2"><SoundToggle /><SaveToGallery svgRef={svgRef} caption={menu ? `${b.menu}: ${menu.name}` : g.free} game="hamburguesa" bg="#FBEFDD" disabled={!stack.length} /><ShareButtons svgRef={svgRef} caption={menu ? `${b.menu}: ${menu.name}` : g.free} file="mi-hamburguesa-pame3d.png" url="https://pame3d.com/hamburguesa" g={g} bg="#FBEFDD" /></div>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6">
            {menu && (
              <div className="bg-white rounded-2xl p-5 border border-black/5 grid sm:grid-cols-2 gap-5" data-testid="game-target">
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#C86A4B] font-bold">{menu.serie}</p>
                  <p className="font-serif-d text-2xl font-semibold text-[#132A1D] mt-1" data-testid="game-target-name">{menu.name}</p>
                  <p className="text-xs text-[#5C5C5C] mt-3 mb-2">{b.order}</p>
                  <ol className="space-y-1" data-testid="burger-target-list">
                    {menu.ingredients.map((ing, i) => {
                      const idx = menu.ingredients.length - 1 - i;
                      const ok = stack[idx] === ing && stack.slice(0, idx).every((k, j) => k === target[j]);
                      return <li key={i} className={`text-sm flex items-center gap-2 ${ok ? "text-[#2E5A3B] font-semibold" : "text-[#132A1D]"}`}><span className={`w-2 h-2 rounded-full ${ok ? "bg-[#2E5A3B]" : "bg-black/15"}`} />{ing}</li>;
                    })}
                  </ol>
                  <p className="text-xs text-[#5C5C5C] mt-4 leading-relaxed"><b>{b.works}</b> {menu.works}</p>
                </div>
                <div>
                  <img src={menu.img} alt={menu.name} loading="lazy" className="w-full aspect-square object-cover rounded-xl" />
                  <ul className="text-xs text-[#5C5C5C] mt-3 space-y-1 list-decimal pl-4">{menu.rules.map((r, i) => <li key={i}>{r}</li>)}</ul>
                </div>
              </div>
            )}
            <div className="bg-white rounded-2xl p-4 border border-black/5">
              <div className="flex flex-wrap gap-2 mb-4">
                {chip("game-undo", () => { if (stack.length) { setStack(stack.slice(0, -1)); sfx.remove(); } }, false, <><Undo2 size={14} /> {g.undo}</>)}
                {chip("game-clear", () => setStack([]), false, <><Trash2 size={14} /> {g.clear}</>)}
                <span className="text-xs text-[#5C5C5C] self-center ml-1">{b.tip}</span>
              </div>
              <div className="grid grid-cols-3 gap-2" data-testid="burger-tray">
                {INGREDIENTS.map((k) => (
                  <button key={k} data-testid={`ing-${k.toLowerCase().replace(/[^a-z]/g, "")}`} onClick={() => add(k)}
                    className="rounded-xl bg-[#FBEFDD] hover:bg-[#F6C744]/40 transition-colors duration-200 p-2 text-left">
                    <LayerIcon name={k} />
                    <span className="text-xs font-semibold text-[#132A1D] block mt-1">{k}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
